/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-native/no-inline-styles */
/**
 * OrderDetails Screen
 * 
 * Displays detailed information about a specific order including:
 * - Order items and quantities
 * - Pricing breakdown (subtotal, taxes, container charges)
 * - Order timeline/tracking history
 * - Supports pull-to-refresh to reload order details
 * 
 * @component
 * @param {Object} navigation - React Navigation object
 * @param {Object} route - Route params containing order_id
 */
import React, { useEffect, useState } from 'react';
import { FlatList, InteractionManager, RefreshControl, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Card, Text } from 'react-native-paper';
import { useDispatch, useSelector } from 'react-redux';
import { enableRefreshControl, fetchOrderDetails, resetData } from '../store/order.details.slice';
import OrderCard from '../components/OrderCard';
import moment from 'moment';
import { theme } from '../theme/theme';
import HttpLoader from '../components/HttpLoader';

/**
 * Main OrderDetails component
 * Fetches and displays order details from API using Redux
 */
function OrderDetails({ navigation, route }) {
    const dispatch = useDispatch();
    const order_id = route?.params?.order_id;
    const [interactionCompleted, setIntersectionCompleted] = useState(false);
    const order = useSelector((state) => state.orderDetails?.order);
    const customer_settings = useSelector((state) => state.customer?.customer?.customer_settings);
    const status = useSelector((state) => state.orderDetails?.status);
    const error = useSelector((state) => state.orderDetails?.error);
    const refreshing = useSelector((state) => state.orderDetails?.refreshing);
    // Data array defining sections to render
    let data = ['order', 'pricing'];
    if (customer_settings?.show_order_timeline) {
        data.push('timeline');
    }

    /**
     * Initialize InteractionManager for smooth transitions
     * Cleanup on unmount
     */
    useEffect(() => {
        const task = InteractionManager.runAfterInteractions(async () => {
            setIntersectionCompleted(true);
        });
        return () => {
            task.cancel();
            dispatch(resetData());
        };
    }, []);

    /**
     * Fetch order details once interaction is complete
     */
    useEffect(() => {
        if (!!interactionCompleted && !!order_id) {
            dispatch(fetchOrderDetails({ order_id }));
        }
    }, [interactionCompleted, order_id]);

    /**
     * Renders refresh control for pull-to-refresh
     * @returns {JSX.Element} RefreshControl component
     */
    const _refreshControl = () => {
        return (
            <RefreshControl
                colors={[theme.colors.primary]}
                refreshing={refreshing}
                onRefresh={() => _onRefresh()}
            />
        );
    };

    /**
     * Handles pull-to-refresh action
     * @param {boolean} retry - Whether this is a retry attempt
     */
    const _onRefresh = (retry) => {
        dispatch(enableRefreshControl({ retry: retry }));
        dispatch(fetchOrderDetails({ order_id }));
    };

    /**
     * Renders individual order tracking timeline item
     * @param {Object} params - FlatList render item params
     * @param {Object} params.item - Order tracking item
     * @param {number} params.index - Item index
     * @returns {JSX.Element} Timeline card
     */
    const renderOrderTrackItem = ({ item, index }) => {
        return (
            <Card.Title
                title={item?.message}
                titleNumberOfLines={null}
                subtitle={moment(item?.changed_at).format('LLL')}
                titleStyle={{ fontSize: 14 }}
                subtitleStyle={{ fontSize: 12 }}
                style={{ borderLeftColor: item.color, borderLeftWidth: 4, margin: 2 }}
            />
        );
    };

    /**
     * Renders the pricing breakdown card with subtotal, taxes, and total
     * Shows SGST, CGST, IGST, and container charges if applicable
     * @returns {JSX.Element} Pricing info card
     */
    const renderPricingInfo = () => {
        return (
            <Card elevation={0}
                theme={{ roundness: 2 }}
                style={{
                    backgroundColor: '#ffffff',
                    margin: 2,
                }}>
                <Card.Content>
                    <Text variant="titleSmall" style={{ fontSize: 14 }}>
                        Pricing Info
                    </Text>
                    <React.Fragment>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 2 }}>
                            <Text style={{ fontSize: 12, color: '#666666' }}>
                                Sub Total
                            </Text>
                            <Text style={{ fontSize: 12, color: '#666666' }}>
                                {order?.currency_symbol}{order?.sub_total}
                            </Text>
                        </View>
                        {order?.container_charges > 0 &&
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 2 }}>
                                <Text style={{ fontSize: 12, color: '#666666' }}>
                                    Container Charges
                                </Text>
                                <Text style={{ fontSize: 12, color: '#666666' }}>
                                    {order?.currency_symbol}{order?.container_charges}
                                </Text>
                            </View>
                        }
                        {order?.sgst > 0 &&
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 2 }}>
                                <Text style={{ fontSize: 12, color: '#666666' }}>
                                    SGST({order?.sgst_percentage}%)
                                </Text>
                                <Text style={{ fontSize: 12, color: '#666666' }}>
                                    {order?.currency_symbol}{order?.sgst}
                                </Text>
                            </View>
                        }
                        {order?.cgst > 0 &&
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 2 }}>
                                <Text style={{ fontSize: 12, color: '#666666' }}>
                                    CGST({order?.cgst_percentage}%)
                                </Text>
                                <Text style={{ fontSize: 12, color: '#666666' }}>
                                    {order?.currency_symbol}{order?.cgst}
                                </Text>
                            </View>
                        }
                        {order?.igst > 0 &&
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 2 }}>
                                <Text style={{ fontSize: 12, color: '#666666' }}>
                                    IGST({order?.igst_percentage}%)
                                </Text>
                                <Text style={{ fontSize: 12, color: '#666666' }}>
                                    {order?.currency_symbol}{order?.igst}
                                </Text>
                            </View>
                        }
                    </React.Fragment>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 2 }}>
                        <Text variant="titleSmall">
                            Total
                        </Text>
                        <Text variant="titleSmall">
                            {order?.currency_symbol}{order?.grand_total}
                        </Text>
                    </View>
                </Card.Content>
            </Card>
        );
    };

    /**
     * Renders main FlatList items (order, pricing, timeline)
     * @param {Object} params - FlatList render item params
     * @param {string} params.item - Item type identifier
     * @param {number} params.index - Item index
     * @returns {JSX.Element|null} Rendered item component
     */
    const renderItem = ({ item, index }) => {
        switch (item) {
            case 'order':
                return <OrderCard order={order} isOrderDetails={true} showOrderNote={true} />;
            case 'pricing':
                return (
                    <React.Fragment>
                        <View style={{ paddingVertical: 8, paddingHorizontal: 2 }}>
                            {renderPricingInfo()}
                        </View>
                    </React.Fragment>
                );
            case 'timeline':
                return (
                    <View style={{ paddingHorizontal: 2 }}>
                        <Card elevation={0} theme={{ roundness: 2 }} style={{
                            backgroundColor: '#ffffff',
                            margin: 2,
                        }}>
                            <Card.Content>
                                <Text variant="titleSmall">Order Timeline</Text>
                            </Card.Content>
                            <FlatList
                                data={order?.order_tracking || []}
                                renderItem={renderOrderTrackItem}
                                keyExtractor={(_item, _index) => (_item.status).toString()}
                            />
                        </Card>
                    </View>
                );
            default:
                return null;
        }
    };

    return (
        <SafeAreaView edges={['bottom']} style={styles.container}>
            {interactionCompleted &&
                <React.Fragment>
                    <HttpLoader status={status} errorHandler={_onRefresh} error={error} />
                    {status === 'succeeded' &&
                        <View style={styles.mainContainer}>
                            {!!order &&
                                <FlatList
                                    data={data}
                                    renderItem={renderItem}
                                    keyExtractor={(item, index) => (index).toString()}
                                    refreshControl={_refreshControl()}
                                />
                            }
                        </View>
                    }
                </React.Fragment>
            }
        </SafeAreaView>
    );
}

/**
 * StyleSheet for OrderDetails component
 */
// 🔹 Styles
const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#f1f1f3',
    },
    mainContainer: {
        flex: 1,
        margin: 8,
    },
    bulkInfoText: {
        paddingVertical: 2,
        textTransform: 'capitalize',
    },
});

export default OrderDetails;
